import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';

import * as d3Scale from 'd3-scale';
import * as d3Array from 'd3-array';
import { TranslateService } from '@ngx-translate/core';
import { tap, takeUntil } from 'rxjs/operators';
import { PercentsService } from '@common/services';

import { DataColumn, DataColumnPoint } from '../../../models';
import { BaseChartComponent } from '../base';

@Component({
    selector: 'aa-gauge-bar',
    templateUrl: './gauge-bar.component.html',
    styleUrls: [
        '../../../container.scss',
        './gauge-bar.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class GaugeBarComponent extends BaseChartComponent<DataColumn> implements OnInit
{
    public xScale: d3Scale.ScaleLinear<number, number>;
    public yScale: d3Scale.ScaleBand<string>;
    public colors: d3Scale.ScaleOrdinal<string, unknown>;
    public colorsHover: d3Scale.ScaleOrdinal<string, unknown>;
    private localData: { x: string; }[];
    private key: string;

    constructor (
        private translateService: TranslateService,
        private percentsService: PercentsService
    )
    {
        super();
    }

    ngOnInit ()
    {
        this.translateService.onLangChange
            .pipe(
                tap(this.setPercentage),
                takeUntil(this.destroyator)
            )
            .subscribe();
    }

    protected drawChart (): void
    {
        this.initSvg();

        this.key = this.data.keys[0];
        this.localData = this.data.points
            .map((p: DataColumnPoint) =>
            {
                const o = { x: p.x };

                for (const [k, v] of p.values)
                {
                    o[k] = v;
                }

                return o;
            });

        this.xScale
            .domain([0, d3Array.max(this.localData, (d: any) => d[this.key])])
            .nice();
        this.yScale
            .domain(this.localData.map((d: any) => d.x));
        this.colors
            .domain(this.data.keys);

        // Rectangle de fond
        this.gpeChart
            .append('g')
            .attr('fill', '#efefef')
            .selectAll('rect')
            .data(this.localData)
            .enter()
            .append('rect')
            .attr('x', 0)
            .attr('y', (d: any) => this.yScale(d.x))
            .attr('height', this.yScale.bandwidth())
            .attr('width', this.xScale(100));

        // Rectangle de données
        this.gpeChart
            .append('g')
            .selectAll('rect')
            .data(this.localData)
            .enter()
            .append('rect')
            .attr('fill', (d: any, idx: number) => this.data.colors[idx])
            .attr('x', 0)
            .attr('y', (d: any) => this.yScale(d.x))
            .attr('height', this.yScale.bandwidth())
            .attr('width', (d: any) => this.xScale(d[this.key]));

        // Legend
        this.gpeChart
            .append('g')
            .selectAll('text')
            .data(this.localData)
            .enter()
            .append('text')
            .attr('fill', (d: any, idx: number) => this.data.colorsHover[idx])
            .attr('text-anchor', 'start')
            .style('font-weight', 'bold')
            .attr('dominant-baseline', 'middle')
            .attr('x', 0)
            .attr('dx', 7)
            .attr('y', (d: any) => this.yScale(d.x) + this.yScale.bandwidth() / 2)
            .attr('dy', 3)
            .text((d: any) => d.x);


        this.setPercentage();
    }

    protected initSvg (): void
    {
        this.baseInitSvg();

        this.xScale = d3Scale
            .scaleLinear()
            .rangeRound([0, this.dimensions.boundedWidth]);
        this.yScale = d3Scale
            .scaleBand()
            .range([this.dimensions.boundedHeight, 0])
            .paddingOuter(0.25)
            .paddingInner(0.25);
        this.colors = d3Scale
            .scaleOrdinal()
            .range(this.data.colors);
        this.colorsHover = d3Scale
            .scaleOrdinal()
            .range(this.data.colorsHover);
    }

    setPercentage = () =>
    {
        this.gpeChart
            .select('g.percentage')
            .remove();
        this.gpeChart
            .append('g')
            .attr('class', 'percentage')
            .selectAll('text')
            .data(this.localData)
            .enter()
            .append('text')
            .attr('fill', (d: any, idx: number) => this.data.colorsHover[idx])
            .attr('text-anchor', 'end')
            .style('font-weight', 'bold')
            .attr('dominant-baseline', 'middle')
            .attr('x', this.xScale(100))
            .attr('dx', -7)
            .attr('y', (d: any) => this.yScale(d.x) + this.yScale.bandwidth() / 2)
            .attr('dy', 3)
            .text((d: any) => this.percentsService.percentage(parseFloat(d[this.key]) / 100, 2));
    };
}
